import { get, mapValues } from 'lodash';
import type { AnyObject } from 'react-final-form';
import ReactGA from 'react-ga';
import type { Primitive } from 'utility-types';

import type { ResolverTag } from '@swf/core/enums';
import { GADimension } from '@swf/core/enums';
import type { BoundTrackers } from '@swf/core/hooks/useTrackers';
import { externalLink } from '@swf/core/utils';
import type { Entity } from '@swf/generated/analytics/entities';
import { widget } from '@swf/generated/analytics/entities';

import type { TrackingContextValue } from '../providers/TrackingContext';

export const GAEvent = (
  params: Omit<Parameters<typeof ReactGA.event>[0], 'category'>,
  { category, widget }: TrackingContextValue,
  dimensions?: Record<string, Primitive | string[]>
) => {
  const allDimensions = { ...dimensions, [GADimension.WidgetName]: widget?.widgetName };
  ReactGA.set(allDimensions);
  ReactGA.event({ ...params, category });
  ReactGA.set(mapValues(allDimensions, () => null));
};

export const signalEvent = ({ tag, data = {} }: { tag: string; data?: AnyObject }) => {
  let customEvent: CustomEvent;
  if (typeof CustomEvent === 'function') {
    customEvent = new CustomEvent(tag, {
      bubbles: true,
      cancelable: true,
      detail: data
    });
    document.dispatchEvent(customEvent);
  } else {
    customEvent = document.createEvent('CustomEvent');
    customEvent.initCustomEvent(tag, true, true, data);
    document.dispatchEvent(customEvent);
  }
};

export const trackableLink = (
  tag: string,
  resolverTag: ResolverTag,
  selfExclusion: BoundTrackers['selfExclusion']
) => externalLink(tag, () => selfExclusion(resolverTag));

// eslint-disable-next-line @typescript-eslint/no-explicit-any
export const memoize = <T extends (...args: any[]) => void>(func: T, path: string) => {
  let cache: Primitive;
  return (...args: Parameters<T>) => {
    const newCache = get(args, path);
    if (newCache !== cache) {
      cache = newCache;
      func(...args);
    }
  };
};

export const getEntities = (context: TrackingContextValue) => {
  const entities: Entity[] = [];
  context.widget && entities.push(widget(context.widget));
  context.entities && entities.push(...context.entities);
  return entities;
};
